"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { RiAddLine } from "react-icons/ri";
import { Button, Input, Label, PhoneNumberInput } from "@/components/ui";
import { queryClient } from "@/config/react-query";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useLocations } from "@/hooks/use-locations";
import { usePatientForm } from "@/hooks/use-patient-form";
import { isPatientMrnEnabledTenant } from "@/lib/patient-mrn";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
import { PatientCreate } from "@/types/patient";
import AddressField from "./address-field";
import CreditCardField, { CreditCardFieldRef } from "./credit-card-field";
import DateOfBirthField from "./dob-field";
import EmailField from "./email-field";
import ExternalIdField from "./external-id-field";
import { getVisibleRequiredPatientFields, isPatientFieldVisible } from "./field-visibility";
import { FIELD_SPAN_CLASS, PATIENT_FORM_FIELDS, type PatientFieldVisibility, type PatientFormFieldSpec, type PatientOptionalFieldKey, nameFieldSpan } from "./fields";
import GenderField from "./gender-field";
import LeadSourceField from "./lead-source-field";
import LocationField from "./location-field";
import MarketingField from "./marketing-field";
import PreferredProviderField from "./preferred-provider-field";
import PatientTextField from "./text-field";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
interface PatientFormProps {
  onSubmit: (patientInfo: PatientCreate) => Promise<boolean>;
  isLoading: boolean;
  buttonLeft?: boolean;
  showCreateButton?: boolean;
  onFormChange?: (data: PatientCreate | null) => void;
  initialData?: Partial<PatientCreate>;
  fieldVisibility?: PatientFieldVisibility;
  phoneNumberRequired?: boolean;
  creditCardRef?: React.RefObject<CreditCardFieldRef | null>;
  submitButtonText?: string;
}
const CreatePatientForm: React.FC<PatientFormProps> = t0 => {
  const $ = _c(107);
  const {
    onSubmit,
    isLoading,
    buttonLeft: t1,
    showCreateButton: t2,
    onFormChange,
    initialData,
    fieldVisibility,
    phoneNumberRequired: t3,
    creditCardRef: externalCreditCardRef,
    submitButtonText
  } = t0;
  const buttonLeft = t1 === undefined ? false : t1;
  const showCreateButton = t2 === undefined ? true : t2;
  const phoneNumberRequired = t3 === undefined ? true : t3;
  const internalCreditCardRef = useRef(null);
  const creditCardRef = externalCreditCardRef || internalCreditCardRef;
  const {
    toast
  } = useToast();
  const {
    tenant
  } = useTenant();
  const {
    currentLocationId
  } = useUser();
  const {
    locations,
    isLocationsLoading
  } = useLocations();
  const [isCreditCardValid, setIsCreditCardValid] = useState(false);
  let t4;
  if ($[0] !== initialData?.email) {
    t4 = () => new Set(initialData?.email ? ["email"] : []);
    $[0] = initialData?.email;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const [addedFields, setAddedFields] = useState(t4);
  const t5 = tenant?.subDomain;
  let t6;
  if ($[2] !== t5) {
    t6 = isPatientMrnEnabledTenant(t5);
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const isExternalIdMrnGenerated = t6;
  let t7;
  if ($[4] !== fieldVisibility) {
    t7 = isPatientFieldVisible(fieldVisibility, "primaryLocation");
    $[4] = fieldVisibility;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const isPrimaryLocationVisible = t7;
  let t8;
  if ($[6] !== fieldVisibility || $[7] !== isExternalIdMrnGenerated || $[8] !== isLocationsLoading || $[9] !== locations || $[10] !== tenant?.requiredPatientFields) {
    let t9;
    if ($[12] !== isExternalIdMrnGenerated) {
      t9 = field => !(isExternalIdMrnGenerated && field === "external_id");
      $[12] = isExternalIdMrnGenerated;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    let t10;
    if ($[14] !== isLocationsLoading || $[15] !== locations) {
      t10 = field_0 => !(field_0 === "primary_location" && !isLocationsLoading && locations.length <= 1);
      $[14] = isLocationsLoading;
      $[15] = locations;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    t8 = getVisibleRequiredPatientFields(tenant?.requiredPatientFields, fieldVisibility).filter(t9).filter(t10);
    $[6] = fieldVisibility;
    $[7] = isExternalIdMrnGenerated;
    $[8] = isLocationsLoading;
    $[9] = locations;
    $[10] = tenant?.requiredPatientFields;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const visibleRequiredPatientFields = t8;
  let t9;
  if ($[17] !== currentLocationId || $[18] !== initialData?.primaryLocationId || $[19] !== isPrimaryLocationVisible) {
    t9 = isPrimaryLocationVisible ? {
      primaryLocationId: initialData?.primaryLocationId ?? currentLocationId ?? ""
    } : {};
    $[17] = currentLocationId;
    $[18] = initialData?.primaryLocationId;
    $[19] = isPrimaryLocationVisible;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== initialData || $[22] !== t9) {
    t10 = {
      ...initialData,
      ...t9
    };
    $[21] = initialData;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const methods = usePatientForm("create", t10, undefined, phoneNumberRequired, visibleRequiredPatientFields);
  const {
    register,
    handleSubmit,
    formState: t11,
    control,
    reset,
    watch
  } = methods;
  const {
    errors
  } = t11;
  let t12;
  let t13;
  if ($[24] !== onFormChange || $[25] !== watch) {
    t12 = () => {
      const subscription = watch(value => {
        if (onFormChange) {
          onFormChange(value as PatientCreate);
        }
      }) as {
        unsubscribe: () => void;
      };
      return () => subscription.unsubscribe();
    };
    t13 = [watch, onFormChange];
    $[24] = onFormChange;
    $[25] = watch;
    $[26] = t12;
    $[27] = t13;
  } else {
    t12 = $[26];
    t13 = $[27];
  }
  useEffect(t12, t13);
  let T0;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  if ($[28] !== addedFields || $[29] !== control || $[30] !== creditCardRef || $[31] !== currentLocationId || $[32] !== errors || $[33] !== fieldVisibility || $[34] !== handleSubmit || $[35] !== isCreditCardValid || $[36] !== isExternalIdMrnGenerated || $[37] !== isPrimaryLocationVisible || $[38] !== locations || $[39] !== methods || $[40] !== onSubmit || $[41] !== phoneNumberRequired || $[42] !== register || $[43] !== reset || $[44] !== tenant?.defaultPatientFields || $[45] !== toast || $[46] !== visibleRequiredPatientFields) {
    const onSubmitForm = async data => {
      if (isPaymentMethodRequired && !isCreditCardValid) {
        toast({
          title: "Credit Card Required",
          description: "Please add a valid credit card to create this patient.",
          variant: "error"
        });
        return;
      }
      if (data.externalId) {
        const isExternalIdUnique = await queryClient.fetchQuery(patientQueryOptions.externalIdUnique(data.externalId));
        if (!isExternalIdUnique) {
          toast({
            title: "External ID already exists",
            description: `A patient with the External ID: "${data.externalId}" already exists in Decoda.`,
            variant: "error"
          });
          return;
        }
      }
      for (const key of ["middleName", "pronouns", "preferredName", "preferredProviderId"] as const) {
        if (!data[key]) {
          data[key] = undefined;
        }
      }
      if (isPrimaryLocationVisible) {
        if (!data.primaryLocationId && currentLocationId) {
          data.primaryLocationId = currentLocationId;
        }
        if (!data.primaryLocationId && locations.length === 1) {
          data.primaryLocationId = locations[0].id;
        }
        if (!data.primaryLocationId) {
          data.primaryLocationId = undefined;
        }
      } else {
        delete data.primaryLocationId;
      }
      const success = await onSubmit(data);
      if (success) {
        reset();
        setIsCreditCardValid(false);
      }
    };
    let t21;
    if ($[55] !== fieldVisibility || $[56] !== isExternalIdMrnGenerated) {
      t21 = field_1 => {
        if (field_1.locked) {
          return true;
        }
        if (field_1.key === "externalId" && isExternalIdMrnGenerated) {
          return false;
        }
        return isPatientFieldVisible(fieldVisibility, field_1.key as PatientOptionalFieldKey);
      };
      $[55] = fieldVisibility;
      $[56] = isExternalIdMrnGenerated;
      $[57] = t21;
    } else {
      t21 = $[57];
    }
    const availableFields = PATIENT_FORM_FIELDS.filter(t21);
    let t22;
    if ($[58] !== tenant?.defaultPatientFields) {
      t22 = tenant?.defaultPatientFields ?? [];
      $[58] = tenant?.defaultPatientFields;
      $[59] = t22;
    } else {
      t22 = $[59];
    }
    let t23;
    if ($[60] !== t22) {
      t23 = new Set(t22);
      $[60] = t22;
      $[61] = t23;
    } else {
      t23 = $[61];
    }
    const shownByDefault = t23;
    let t24;
    if ($[62] !== phoneNumberRequired || $[63] !== visibleRequiredPatientFields) {
      t24 = field_2 => field_2.locked ? field_2.key !== "phoneNumber" || phoneNumberRequired : visibleRequiredPatientFields.includes(field_2.requiredKey);
      $[62] = phoneNumberRequired;
      $[63] = visibleRequiredPatientFields;
      $[64] = t24;
    } else {
      t24 = $[64];
    }
    const isFieldRequired = t24;
    let t25;
    if ($[65] !== addedFields || $[66] !== fieldVisibility || $[67] !== shownByDefault || $[68] !== visibleRequiredPatientFields) {
      t25 = field_3 => field_3.locked || visibleRequiredPatientFields.includes(field_3.requiredKey) || shownByDefault.has(field_3.requiredKey) || fieldVisibility?.[field_3.key as PatientOptionalFieldKey] === true || addedFields.has(field_3.key as PatientOptionalFieldKey);
      $[65] = addedFields;
      $[66] = fieldVisibility;
      $[67] = shownByDefault;
      $[68] = visibleRequiredPatientFields;
      $[69] = t25;
    } else {
      t25 = $[69];
    }
    const isFieldOnForm = t25;
    const shownFields = availableFields.filter(isFieldOnForm);
    let t26;
    if ($[70] !== isFieldOnForm) {
      t26 = field_4 => !isFieldOnForm(field_4);
      $[70] = isFieldOnForm;
      $[71] = t26;
    } else {
      t26 = $[71];
    }
    const addableFields = availableFields.filter(t26);
    const nameSpan = nameFieldSpan(shownFields.some(_temp));
    const isPaymentMethodRequired = visibleRequiredPatientFields.includes("payment_method");
    let t27;
    if ($[72] !== isPrimaryLocationVisible || $[73] !== visibleRequiredPatientFields) {
      t27 = isPrimaryLocationVisible && visibleRequiredPatientFields.includes("primary_location");
      $[72] = isPrimaryLocationVisible;
      $[73] = visibleRequiredPatientFields;
      $[74] = t27;
    } else {
      t27 = $[74];
    }
    const isPrimaryLocationRequired = t27;
    let t28;
    if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = key_0 => setAddedFields(current => new Set(current).add(key_0));
      $[75] = t28;
    } else {
      t28 = $[75];
    }
    const addField = t28;
    let t29;
    if ($[76] !== control || $[77] !== creditCardRef || $[78] !== errors || $[79] !== isFieldRequired || $[80] !== register) {
      t29 = field_6 => {
        const required = isFieldRequired(field_6);
        const label = <Label htmlFor={field_6.key} mandatory={required}>{field_6.label}</Label>;
        switch (field_6.key) {
          case "firstName":
            {
              return <>{label}<Input type="text" id="firstName" autoComplete="given-name" placeholder="John" className="mt-1" {...register("firstName")} hasError={!!errors.firstName} errorMessage={errors.firstName?.message} /></>;
            }
          case "middleName":
            {
              return <>{label}<PatientTextField name="middleName" placeholder="Quincy" /></>;
            }
          case "lastName":
            {
              return <>{label}<Input type="text" id="lastName" autoComplete="family-name" placeholder="Smith" className="mt-1" {...register("lastName")} hasError={!!errors.lastName} errorMessage={errors.lastName?.message} /></>;
            }
          case "phoneNumber":
            {
              return <>{label}<Controller name="phoneNumber" control={control} render={t30 => {
                  const {
                    field: phoneField
                  } = t30;
                  return <PhoneNumberInput {...phoneField} hasError={!!errors.phoneNumber} errorMessage={errors.phoneNumber?.message} className="mt-1" />;
                }} /></>;
            }
          case "email":
            {
              return <>{label}<EmailField required={required} /></>;
            }
          case "dateOfBirth":
            {
              return <>{label}<DateOfBirthField required={required} /></>;
            }
          case "gender":
            {
              return <>{label}<GenderField required={required} /></>;
            }
          case "pronouns":
            {
              return <>{label}<PatientTextField name="pronouns" placeholder="she/her" /></>;
            }
          case "preferredName":
            {
              return <>{label}<PatientTextField name="preferredName" placeholder="Nickname" /></>;
            }
          case "externalId":
            {
              return <>{label}<ExternalIdField required={required} /></>;
            }
          case "preferredProvider":
            {
              return <>{label}<PreferredProviderField /></>;
            }
          case "leadSource":
            {
              return <LeadSourceField required={required} />;
            }
          case "address":
            {
              return <AddressField required={required} />;
            }
          case "marketing":
            {
              return <MarketingField required={required} />;
            }
          case "paymentMethod":
            {
              return <>{label}<div className="mt-1"><CreditCardField ref={creditCardRef} onValidityChange={setIsCreditCardValid} /></div></>;
            }
        }
      };
      $[76] = control;
      $[77] = creditCardRef;
      $[78] = errors;
      $[79] = isFieldRequired;
      $[80] = register;
      $[81] = t29;
    } else {
      t29 = $[81];
    }
    const renderField = t29;
    T0 = FormProvider;
    t20 = methods;
    t14 = "#";
    t15 = "post";
    t16 = "mt-4";
    if ($[82] !== handleSubmit || $[83] !== onSubmitForm || $[84] !== toast) {
      t17 = event => {
        event.stopPropagation();
        handleSubmit(onSubmitForm)(event).catch(error => {
          console.error("Create patient form submit failed:", error);
          toast({
            title: "Error",
            description: "Something went wrong. Please try again.",
            variant: "error"
          });
        });
      };
      $[82] = handleSubmit;
      $[83] = onSubmitForm;
      $[84] = toast;
      $[85] = t17;
    } else {
      t17 = $[85];
    }
    let t30;
    if ($[86] !== isPrimaryLocationRequired || $[87] !== isPrimaryLocationVisible) {
      t30 = isPrimaryLocationVisible && <LocationField required={isPrimaryLocationRequired} className="md:col-span-3" />;
      $[86] = isPrimaryLocationRequired;
      $[87] = isPrimaryLocationVisible;
      $[88] = t30;
    } else {
      t30 = $[88];
    }
    t18 = <div className="grid grid-cols-1 gap-x-4 gap-y-5 md:grid-cols-6">{shownFields.map(field_7 => <div key={field_7.key} className={FIELD_SPAN_CLASS[field_7.key === "firstName" || field_7.key === "lastName" ? nameSpan : field_7.span]}>{renderField(field_7)}</div>)}{t30}</div>;
    t19 = addableFields.length > 0 && <div className="mt-6"><p className="text-caption-strong text-muted-foreground">Add more details</p><div className="mt-2 flex flex-wrap gap-2">{addableFields.map(field_8 => <Button key={field_8.key} type="button" variant="light" size="sm" icon={RiAddLine} onClick={() => addField(field_8.key as PatientOptionalFieldKey)}>{field_8.label}</Button>)}</div></div>;
    $[28] = addedFields;
    $[29] = control;
    $[30] = creditCardRef;
    $[31] = currentLocationId;
    $[32] = errors;
    $[33] = fieldVisibility;
    $[34] = handleSubmit;
    $[35] = isCreditCardValid;
    $[36] = isExternalIdMrnGenerated;
    $[37] = isPrimaryLocationVisible;
    $[38] = locations;
    $[39] = methods;
    $[40] = onSubmit;
    $[41] = phoneNumberRequired;
    $[42] = register;
    $[43] = reset;
    $[44] = tenant?.defaultPatientFields;
    $[45] = toast;
    $[46] = visibleRequiredPatientFields;
    $[47] = T0;
    $[48] = t14;
    $[49] = t15;
    $[50] = t16;
    $[51] = t17;
    $[52] = t18;
    $[53] = t19;
    $[54] = t20;
  } else {
    T0 = $[47];
    t14 = $[48];
    t15 = $[49];
    t16 = $[50];
    t17 = $[51];
    t18 = $[52];
    t19 = $[53];
    t20 = $[54];
  }
  let t21;
  if ($[89] !== buttonLeft || $[90] !== initialData || $[91] !== isLoading || $[92] !== showCreateButton || $[93] !== submitButtonText) {
    t21 = showCreateButton && <div className={cx("mt-6 flex items-center gap-x-6", {
      "justify-end": !buttonLeft
    })}><Button variant="primary" type="submit" isLoading={isLoading} loadingText="Creating...">{submitButtonText ?? (initialData ? "Save Changes" : "Create Patient")}</Button></div>;
    $[89] = buttonLeft;
    $[90] = initialData;
    $[91] = isLoading;
    $[92] = showCreateButton;
    $[93] = submitButtonText;
    $[94] = t21;
  } else {
    t21 = $[94];
  }
  let t22;
  if ($[95] !== t14 || $[96] !== t15 || $[97] !== t16 || $[98] !== t17 || $[99] !== t18 || $[100] !== t19 || $[101] !== t21) {
    t22 = <form action={t14} method={t15} className={t16} onSubmit={t17}>{t18}{t19}{t21}</form>;
    $[95] = t14;
    $[96] = t15;
    $[97] = t16;
    $[98] = t17;
    $[99] = t18;
    $[100] = t19;
    $[101] = t21;
    $[102] = t22;
  } else {
    t22 = $[102];
  }
  let t23;
  if ($[103] !== T0 || $[104] !== t20 || $[105] !== t22) {
    t23 = <T0 {...t20}>{t22}</T0>;
    $[103] = T0;
    $[104] = t20;
    $[105] = t22;
    $[106] = t23;
  } else {
    t23 = $[106];
  }
  return t23;
};
export default CreatePatientForm;
function _temp(field_5) {
  return field_5.key === "middleName";
}